export type WatcherSession = {
  userId: string;
  token: string;
};

export type WatcherPayoutInfo = {
  userAddress: string;
  fppsAddress: string;
  pplnsAddress: string;
};

export type SingleWatcherLink = {
  session: WatcherSession;
  payoutInfo: WatcherPayoutInfo | null;
};

export type MultiWatcherRequirement =
  | "hashrate"
  | "generated_btc"
  | "generated_btc_hashrate";

export type MultiWatcherLink = {
  requirement: MultiWatcherRequirement;
  sessions: WatcherSession[];
};

export type WatcherBootstrap = {
  watcherSession: WatcherSession | null;
  watcherAuthResponses: WatcherSession[];
  multiWatcherRequirement: MultiWatcherRequirement;
  watcherPayoutInfo: WatcherPayoutInfo | null;
  usesSavedMultiWatcherSessions: boolean;
};

export const DEFAULT_MULTI_WATCHER_REQUIREMENT: MultiWatcherRequirement =
  "generated_btc_hashrate";

const WATCHER_PAYOUT_VERSION = "1";
const MIN_BITCOIN_ADDRESS_LENGTH = 14;
const MAX_BITCOIN_ADDRESS_LENGTH = 128;
const MAX_WATCHER_PAYOUT_HASH_LENGTH = 8192;

function routeSegmentsFromPathname(pathname: string): string[] {
  const segments = pathname.split("/").filter(Boolean);
  return segments[0] === "static" ? segments.slice(1) : segments;
}

export function isSingleAuthPathname(pathname: string): boolean {
  const pathSegments = routeSegmentsFromPathname(pathname);
  if (pathSegments.length === 0) {
    return false;
  }

  if (pathSegments[0] === "login") {
    return pathSegments[1] !== "multiwatcher" && pathSegments[1] !== "watcher";
  }

  return pathSegments[0] === "broker" && pathSegments[1] === "login";
}

export function isWatcherAuthPathIntent(pathname: string): boolean {
  const pathSegments = routeSegmentsFromPathname(pathname);
  return (
    pathSegments[0] === "multiwatcher" ||
    (pathSegments[0] === "login" &&
      (pathSegments[1] === "watcher" || pathSegments[1] === "multiwatcher"))
  );
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).trim();
  } catch (_) {
    return segment.trim();
  }
}

function sessionFromSegments(userIdSegment: string, tokenSegment: string): WatcherSession | null {
  const userId = decodePathSegment(userIdSegment);
  const token = decodePathSegment(tokenSegment);

  if (!userId || !token) {
    return null;
  }

  return { userId, token };
}

function normalizedBitcoinAddress(value: string | null): string | null {
  if (!value || value !== value.trim()) {
    return null;
  }

  if (
    value.length < MIN_BITCOIN_ADDRESS_LENGTH ||
    value.length > MAX_BITCOIN_ADDRESS_LENGTH ||
    !/^[A-Za-z0-9]+$/.test(value)
  ) {
    return null;
  }

  return value;
}

export function parseWatcherPayoutInfo(value: unknown): WatcherPayoutInfo | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Partial<WatcherPayoutInfo>;
  const userAddress = normalizedBitcoinAddress(
    typeof candidate.userAddress === "string" ? candidate.userAddress : null
  );
  const rawFppsAddress =
    typeof candidate.fppsAddress === "string" ? candidate.fppsAddress : null;
  const rawPplnsAddress =
    typeof candidate.pplnsAddress === "string" ? candidate.pplnsAddress : null;
  const fppsAddress =
    rawFppsAddress === "" ? "" : normalizedBitcoinAddress(rawFppsAddress);
  const pplnsAddress =
    rawPplnsAddress === "" ? "" : normalizedBitcoinAddress(rawPplnsAddress);

  if (
    !userAddress ||
    fppsAddress === null ||
    pplnsAddress === null ||
    (!fppsAddress && !pplnsAddress)
  ) {
    return null;
  }

  return { userAddress, fppsAddress, pplnsAddress };
}

function exactlyOneParam(params: URLSearchParams, key: string): string | null {
  const values = params.getAll(key);
  return values.length === 1 ? values[0] : null;
}

function payoutInfoFromHash(hash: string, watcherUserId: string): WatcherPayoutInfo | null {
  if (hash.length > MAX_WATCHER_PAYOUT_HASH_LENGTH) {
    return null;
  }

  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!fragment) {
    return null;
  }

  const params = new URLSearchParams(fragment);
  if (
    exactlyOneParam(params, "payoutVersion") !== WATCHER_PAYOUT_VERSION ||
    exactlyOneParam(params, "payoutUserId") !== watcherUserId
  ) {
    return null;
  }

  return parseWatcherPayoutInfo({
    userAddress: exactlyOneParam(params, "userAddress"),
    fppsAddress: exactlyOneParam(params, "fppsAddress"),
    pplnsAddress: exactlyOneParam(params, "pplnsAddress"),
  });
}

export function getSingleWatcherLink(
  pathname: string,
  hash: string
): SingleWatcherLink | null {
  const routeSegments = routeSegmentsFromPathname(pathname);
  if (routeSegments.join("/") === "login/watcher") {
    if (hash.length > MAX_WATCHER_PAYOUT_HASH_LENGTH) return null;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const userId = exactlyOneParam(params, "userId");
    const token = exactlyOneParam(params, "token");
    if (!userId || !token || !userId.trim() || !token.trim()) return null;
    return {
      session: { userId, token },
      payoutInfo: payoutInfoFromHash(hash, userId),
    };
  }
  if (
    routeSegments.length !== 4 ||
    routeSegments[0] !== "login" ||
    routeSegments[1] !== "watcher"
  ) {
    return null;
  }

  const session = sessionFromSegments(routeSegments[2], routeSegments[3]);
  if (!session) {
    return null;
  }

  return {
    session,
    payoutInfo: payoutInfoFromHash(hash, session.userId),
  };
}

function sessionsFromPairSegments(pairSegments: string[]): WatcherSession[] | null {
  if (pairSegments.length < 2 || pairSegments.length % 2 !== 0) {
    return null;
  }

  const sessions: WatcherSession[] = [];
  for (let i = 0; i < pairSegments.length; i += 2) {
    const session = sessionFromSegments(pairSegments[i], pairSegments[i + 1]);
    if (!session) {
      return null;
    }
    sessions.push(session);
  }

  return sessions;
}

function requirementFromSegment(segment: string): MultiWatcherRequirement | null {
  const decoded = decodePathSegment(segment);
  if (
    decoded === "hashrate" ||
    decoded === "generated_btc" ||
    decoded === "generated_btc_hashrate"
  ) {
    return decoded;
  }

  return null;
}

function linkFromSegments(segments: string[]): MultiWatcherLink | null {
  if (segments.length === 0) {
    return null;
  }

  const requirement = requirementFromSegment(segments[0]);
  const sessions = sessionsFromPairSegments(requirement ? segments.slice(1) : segments);

  if (!sessions) {
    return null;
  }

  return {
    requirement: requirement ?? DEFAULT_MULTI_WATCHER_REQUIREMENT,
    sessions,
  };
}

export function getMultiWatcherLinkFromPathname(pathname: string, hash = ""): MultiWatcherLink | null {
  const routeSegments = routeSegmentsFromPathname(pathname);

  if (["multiwatcher", "login/multiwatcher"].includes(routeSegments.join("/"))) {
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const pairs = exactlyOneParam(params, "sessions");
    const requirement = exactlyOneParam(params, "requirement");
    if (!pairs || !requirement || !requirementFromSegment(requirement)) return null;
    const sessions = sessionsFromPairSegments(pairs.split("/"));
    if (!sessions) return null;
    return { requirement: requirement as MultiWatcherRequirement, sessions };
  }

  if (routeSegments[0] === "multiwatcher") {
    return linkFromSegments(routeSegments.slice(1));
  }

  if (routeSegments[0] === "login" && routeSegments[1] === "multiwatcher") {
    return linkFromSegments(routeSegments.slice(2));
  }

  return null;
}

export function shouldStartWithoutStoredWatcherState(pathname: string, hash = ""): boolean {
  if (routeSegmentsFromPathname(pathname).join("/") === "multiwatcher" && !hash) return false;
  return (
    isSingleAuthPathname(pathname) ||
    (isWatcherAuthPathIntent(pathname) &&
      !getSingleWatcherLink(pathname, hash) &&
      !getMultiWatcherLinkFromPathname(pathname, hash))
  );
}

/** Keep shareable credentials in fragments, never in HTTP paths or history state. */
export function consumeWatcherLocation(
  location: Pick<Location, "pathname" | "hash">,
  history: Pick<History, "replaceState">
) {
  const singleWatcherLink = getSingleWatcherLink(location.pathname, location.hash);
  const multiWatcherLink = getMultiWatcherLinkFromPathname(location.pathname, location.hash);
  const startsWithoutStoredWatcherState = shouldStartWithoutStoredWatcherState(location.pathname, location.hash);
  if (isWatcherAuthPathIntent(location.pathname) &&
      !(routeSegmentsFromPathname(location.pathname).join("/") === "multiwatcher" && !location.hash)) {
    const cleanPath = watcherLinkUrl(singleWatcherLink, multiWatcherLink, watcherPagePath(location.pathname, location.hash));
    history.replaceState(null, "", cleanPath);
  }
  return { singleWatcherLink, multiWatcherLink, startsWithoutStoredWatcherState };
}

const WATCHER_PAGES = ["/home", "/workers", "/generated-bitcoin", "/payouts", "/pplns-projection", "/multiwatcher"];

export function isWatcherPage(path: string): boolean {
  return WATCHER_PAGES.includes(path.replace(/^\/static/, ""));
}

/** Present the embedded route to Elm while retaining the reusable watcher URL. */
export function watcherPagePath(pathname: string, hash: string): string {
  if (!isWatcherAuthPathIntent(pathname)) return pathname;
  const page = new URLSearchParams(hash.replace(/^#/, "")).get("page");
  if (page && WATCHER_PAGES.includes(page)) return page;
  const multi = getMultiWatcherLinkFromPathname(pathname, hash);
  return multi ? multi.requirement === "hashrate" ? "/workers" : "/multiwatcher" : "/home";
}

export function watcherLinkUrl(single: SingleWatcherLink | null, multi: MultiWatcherLink | null, page?: string): string {
  const params = new URLSearchParams();
  const defaultPage = multi ? multi.requirement === "hashrate" ? "/workers" : "/multiwatcher" : "/home";
  if (multi) {
    params.set("requirement", multi.requirement);
    params.set("sessions", multi.sessions.flatMap(session => [encodeURIComponent(session.userId), encodeURIComponent(session.token)]).join("/"));
  } else if (single) {
    params.set("userId", single.session.userId);
    params.set("token", single.session.token);
    if (single.payoutInfo) {
      params.set("payoutVersion", "1");
      params.set("payoutUserId", single.session.userId);
      for (const [key, value] of Object.entries(single.payoutInfo)) params.set(key, value);
    }
  } else return "/login";
  if (page && page !== defaultPage && WATCHER_PAGES.includes(page)) params.set("page", page);
  return `/login/${multi ? "multiwatcher" : "watcher"}#${params.toString()}`;
}

/** Navigation retains only active tab-local watcher credentials, not user cookies. */
export function watcherNavigationUrl(path: string, storage: Pick<Storage, "getItem">): string {
  const page = path.replace(/^\/static/, "");
  if (!isWatcherPage(path)) return path;
  try {
    const raw = JSON.parse(storage.getItem("watcher_session") || "null");
    if (!raw || typeof raw.userId !== "string" || typeof raw.token !== "string" || !raw.userId || !raw.token) return path;
    const sessions = JSON.parse(storage.getItem("multiwatcher_sessions") || "null");
    const requirement = requirementFromSegment(storage.getItem("multiwatcher_requirement") || "") ?? DEFAULT_MULTI_WATCHER_REQUIREMENT;
    const multi = Array.isArray(sessions) && sessions.length && sessions.every(session => session && typeof session.userId === "string" && typeof session.token === "string")
      ? { sessions, requirement } : null;
    return watcherLinkUrl({ session: { userId: raw.userId, token: raw.token }, payoutInfo: parseWatcherPayoutInfo(raw.payoutInfo) }, multi, page);
  } catch (_) { return path; }
}

export function selectWatcherBootstrap({
  multiWatcherLink,
  singleWatcherLink,
  savedMultiWatcherSessions,
  savedSingleWatcherLink,
  savedMultiWatcherRequirement,
}: {
  multiWatcherLink: MultiWatcherLink | null;
  singleWatcherLink: SingleWatcherLink | null;
  savedMultiWatcherSessions: WatcherSession[];
  savedSingleWatcherLink: SingleWatcherLink | null;
  savedMultiWatcherRequirement: MultiWatcherRequirement;
}): WatcherBootstrap {
  const multiWatcherSessions = multiWatcherLink?.sessions ?? null;
  const watcherSessionFromPath = singleWatcherLink?.session ?? null;
  const usesSavedMultiWatcherSessions =
    !multiWatcherSessions &&
    !watcherSessionFromPath &&
    savedMultiWatcherSessions.length > 0;
  const multiWatcherRequirement =
    multiWatcherLink?.requirement ??
    (usesSavedMultiWatcherSessions
      ? savedMultiWatcherRequirement
      : DEFAULT_MULTI_WATCHER_REQUIREMENT);
  const watcherAuthResponses =
    multiWatcherSessions ??
    (watcherSessionFromPath
      ? [watcherSessionFromPath]
      : savedMultiWatcherSessions);
  const watcherSession =
    multiWatcherSessions?.[0] ??
    watcherSessionFromPath ??
    savedMultiWatcherSessions[0] ??
    savedSingleWatcherLink?.session ??
    null;
  const watcherPayoutInfo =
    multiWatcherSessions || usesSavedMultiWatcherSessions
      ? null
      : singleWatcherLink
        ? singleWatcherLink.payoutInfo
        : savedSingleWatcherLink?.payoutInfo ?? null;

  return {
    watcherSession,
    watcherAuthResponses,
    multiWatcherRequirement,
    watcherPayoutInfo,
    usesSavedMultiWatcherSessions,
  };
}
